"use client";

import React, { useEffect } from "react";
import { X } from "lucide-react";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  position?: "left" | "right";
  className?: string;
  isEscKey?: boolean;
  isOverlayClick?: boolean;
  variant?: "light" | "dark";
  width?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  position = "right",
  className = "",
  isEscKey = false,
  isOverlayClick = false,
  variant = "light",
  width = "sm:w-96",
}) => {
  useEffect(() => {
    if (isOpen) {
      const scrollBarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isEscKey) {
      window.addEventListener("keydown", handleEsc);
    }
    return () => {
      if (isEscKey) {
        window.removeEventListener("keydown", handleEsc);
      }
    };
  }, [isEscKey, onClose]);

  const isLeft = position === "left";

  return (
    <>
      <div
        className={`fixed h-full inset-0 z-[2000] bg-black/80 backdrop-blur-sm transition-opacity duration-300 no-print ${isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`}
        onClick={isOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        className={`fixed top-0 bottom-0 z-[2001] w-[85vw] ${width} shadow-2xl transition-transform duration-300 ease-in-out no-print 
          ${isLeft ? "left-0 border-r" : "right-0 border-l"} 
          ${isOpen ? "translate-x-0" : isLeft ? "-translate-x-full" : "translate-x-full"} 
          ${variant === "light" ? "bg-white border-slate-200" : "bg-black-2 border-border-gold"} 
          ${className}`}
      >
        <div className="flex flex-col h-full">
          <div
            className={`p-6 flex items-center justify-between border-b ${variant === "light" ? "border-slate-100" : "border-white/5"}`}
          >
            {title && (
              <h2
                className={`font-cormorant text-xl font-bold tracking-widest uppercase ${variant === "light" ? "text-slate-800" : "gold-text-gradient"}`}
              >
                {title}
              </h2>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className={`${variant === "light" ? "text-slate-400 hover:text-slate-600" : "text-text-3 hover:text-gold-core"} transition-colors p-2 cursor-pointer`}
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto w-full p-6">{children}</div>
        </div>
      </div>
    </>
  );
};
